import React from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

interface PayloadItem {
  payload: {
    fullTimestamp: string;
    ts: string;
    device_sensor: string;
    reds_percentage: number;
  };
  value: number;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: PayloadItem[];
  label?: string;
  groupedMovingAverage?: any[];
  viewMode?: 'default' | 'individual';
  individualSensorToggles?: Record<
    string,
    { actual: boolean; movingAverage: boolean }
  >;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active = false,
  payload = [],
  label = '',
  groupedMovingAverage,
  viewMode = 'default',
  individualSensorToggles = {},
}) => {
  const { t } = useTranslation();
  if (active && payload.length) {
    const startTime = dayjs(parseInt(payload[0].payload.ts)).format(
      'YYYY-MM-DD, h:mm A'
    );
    const endTime = dayjs(parseInt(payload[0].payload.ts) + 3600000).format(
      'YYYY-MM-DD, h:mm A'
    );

    if (viewMode === 'individual') {
      const allSensors =
        groupedMovingAverage
          ?.filter((item) => item.ts.N === payload[0].payload.ts)
          .map((item) => {
            const sensorName =
              item.device_sensor.S.split('-')[1].replace(/_/g, ' ') ||
              'Unknown Sensor';
            return {
              sensor: sensorName,
              redsPercentage: item.reds_percentage.N || '0',
              avgValue: item.avg_val.N || '0',
            };
          }) ?? [];

      // Filter sensors to only show those with active lines
      const displayedSensors = allSensors.filter((sensor) => {
        const toggleState = individualSensorToggles[sensor.sensor];
        return toggleState?.actual || toggleState?.movingAverage;
      });

      return (
        <div
          style={{
            backgroundColor: 'rgb(28, 58, 101)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '8px',
            color: 'white',
            fontSize: '12px',
            maxWidth: '200px',
            padding: '5px 7px',
            marginTop: '150px',
          }}
        >
          <p style={{ fontWeight: 'bold' }}>
            {t('start_time')}{' '}
            <span style={{ fontSize: '11px' }}>{startTime}</span>
          </p>
          <p style={{ fontWeight: 'bold' }}>
            {t('end_time')} <span style={{ fontSize: '11px' }}>{endTime}</span>
          </p>

          <div
            style={{
              backgroundColor: 'rgb(255, 255, 255)',
              border: '1px solid rgba(234, 225, 225, 0.2)',
              borderRadius: '8px',
              padding: '1px 10px 5px 10px',
              color: 'black',
              fontWeight: 'bold',
              marginTop: '8px',
            }}
          >
            <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>
              {t('sensor_values')}
            </p>
            <ul
              style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                fontWeight: 'normal',
              }}
            >
              {displayedSensors.map((sensor, index) => {
                const toggleState = individualSensorToggles[sensor.sensor];
                return (
                  <li key={index}>
                    <div style={{ marginBottom: '4px' }}>
                      <strong>{sensor.sensor}:</strong>
                    </div>
                    {toggleState?.actual && (
                      <div
                        style={{
                          marginLeft: '10px',
                          fontSize: '11px',
                          color: '#2196F3',
                        }}
                      >
                        {t('actual')} {sensor.redsPercentage}%
                      </div>
                    )}
                    {toggleState?.movingAverage && (
                      <div
                        style={{
                          marginLeft: '10px',
                          fontSize: '11px',
                          color: '#F44336',
                        }}
                      >
                        {t('avg')} {sensor.avgValue}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      );
    }

    const actualValue = payload[0]?.value;
    const movingAvgValue = payload[1]?.value;

    const sensors = groupedMovingAverage
      ?.filter((item) => item.ts.N === payload[0].payload.ts)
      .map((item) => {
        return {
          sensor:
            item.device_sensor.S.split('-')[1].replace(/_/g, '') ||
            'Unknown Sensor',
          redsPercentage: item.reds_percentage.N || '0',
        };
      }) ?? [
      {
        sensor: 'Unknown Sensor',
        redsPercentage: '0',
      },
    ];

    return (
      <div
        style={{
          backgroundColor: 'rgb(28, 58, 101)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '8px',
          color: 'white',
          fontSize: '12px',
          maxWidth: '200px',
          padding: '5px 7px',
          marginTop: '150px',
        }}
      >
        <p style={{ fontWeight: 'bold' }}>
          {t('start_time')}{' '}
          <span style={{ fontSize: '11px' }}>{startTime}</span>
        </p>
        <p style={{ fontWeight: 'bold' }}>
          {t('end_time')} <span style={{ fontSize: '11px' }}>{endTime}</span>
        </p>
        <p style={{ color: '#2196F3' }}>
          {t('actual_value_2')}{' '}
          <span style={{ fontSize: '11px' }}>{actualValue.toFixed(4)}</span>{' '}
        </p>
        <p style={{ color: '#F44336' }}>
          {t('moving_average_value')}{' '}
          <span style={{ fontSize: '11px' }}>{movingAvgValue?.toFixed(4)}</span>
        </p>

        <div
          style={{
            backgroundColor: 'rgb(255, 255, 255)',
            border: '1px solid rgba(234, 225, 225, 0.2)',
            borderRadius: '8px',
            padding: '1px 10px 5px 10px',
            color: 'black',
            fontWeight: 'bold',
          }}
        >
          <p style={{ fontWeight: 'bold' }}>
            {t('sensors_in')} <span style={{ color: '#F44336' }}>Red</span>{' '}
            {t('zone')}
          </p>
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              fontWeight: 'normal',
            }}
          >
            {sensors.map((sensor, index) => (
              <li key={index}>
                {sensor.sensor}: {sensor.redsPercentage}%
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  return null;
};

CustomTooltip.defaultProps = {
  active: false,
  payload: [],
  label: '',
  viewMode: 'default',
  individualSensorToggles: {},
};

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(
    PropTypes.shape({
      payload: PropTypes.shape({
        fullTimestamp: PropTypes.string.isRequired,
        ts: PropTypes.string.isRequired,
        device_sensor: PropTypes.string.isRequired,
        reds_percentage: PropTypes.number.isRequired,
      }).isRequired,
      value: PropTypes.number.isRequired,
    }).isRequired
  ),
  label: PropTypes.string,
  groupedMovingAverage: PropTypes.array,
  viewMode: PropTypes.oneOf(['default', 'individual']),
};

export default CustomTooltip;
